import { useCallback, useState } from 'react';
import { useApiFetch } from '../api';
import type { DomainAvailabilityResult } from '../types/models/DomainAvailability';
import type { ApiResponseDomainAvailability } from '../types/api/ApiResponseDomainAvailability';

interface UseDomainAvailabilityReturn {
  results: DomainAvailabilityResult[];
  loading: boolean;
  error: string | null;
  /** True once at least one search has completed — distinguishes "no results" from "not searched yet". */
  hasSearched: boolean;
  /** Lazy availability check. `domain` may be a bare label or a full FQDN; `tlds` is ignored for an FQDN. */
  search: (domain: string, tlds: string[]) => Promise<void>;
  /** Clears results/error so the search panel returns to its pristine state. */
  reset: () => void;
}

/**
 * `GET /datacenters/domain-names/availability?domain=<label|fqdn>&tlds=<csv>` — a **lazy** hook (no
 * fetch on mount). Call `search()` from a button. JSend errors arrive at HTTP 200 with
 * `status: 'error'` (field errors under `data.domain` / `data.tlds`), so success is `body.status ===
 * 'success'` plus a `results` array — never `response.ok`.
 */
export const useDomainAvailability = (): UseDomainAvailabilityReturn => {
  const apiFetch = useApiFetch();
  const [results, setResults] = useState<DomainAvailabilityResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const search = useCallback(
    async (domain: string, tlds: string[]) => {
      const trimmed = domain.trim();
      if (trimmed === '') {
        setError('Enter a domain name or label to search.');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ domain: trimmed });
        if (tlds.length > 0) {
          params.set('tlds', tlds.join(','));
        }
        const response = await apiFetch(`/datacenters/domain-names/availability?${params.toString()}`);
        const body = (await response.json().catch(() => null)) as ApiResponseDomainAvailability | null;

        if (body?.status === 'success' && Array.isArray(body.data?.results)) {
          setResults(body.data.results);
          setHasSearched(true);
        } else {
          const fieldError =
            (typeof body?.data?.domain === 'string' ? body.data.domain : null) ??
            (typeof body?.data?.tlds === 'string' ? body.data.tlds : null);
          setError(fieldError ?? body?.message ?? 'The availability check failed. Please try again.');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred while checking availability.');
      } finally {
        setLoading(false);
      }
    },
    [apiFetch],
  );

  const reset = useCallback(() => {
    setResults([]);
    setError(null);
    setHasSearched(false);
  }, []);

  return { results, loading, error, hasSearched, search, reset };
};
